/**
 * QR Code Component
 *
 * Generates QR codes for rooms, services, and direct links.
 * Supports download and clipboard copy functionality.
 */

import { useState, useCallback } from 'react'
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react'
import { Download, Copy, Check, QrCode } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button, Modal } from './ui'
import polygrafIcon from '@/assets/polygraf-icon.svg'

interface QRCodeDisplayProps {
	/** The URL or data to encode */
	value: string
	/** Title shown above the QR code */
	title?: string
	/** Size of the QR code in pixels */
	size?: number
	/** Whether to include the Polygraf logo in center */
	includeLogo?: boolean
	/** Callback when QR code is copied */
	onCopy?: () => void
	/** Callback when QR code is downloaded */
	onDownload?: () => void
}

/**
 * QR Code display component with download and copy functionality
 */
export function QRCodeDisplay({
	value,
	title,
	size = 200,
	includeLogo = false,
	onCopy,
	onDownload,
}: QRCodeDisplayProps) {
	const { t } = useTranslation()
	const [copied, setCopied] = useState(false)

	const handleCopyUrl = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(value)
			setCopied(true)
			onCopy?.()
			setTimeout(() => setCopied(false), 2000)
		} catch (err) {
			console.error('Failed to copy URL:', err)
		}
	}, [value, onCopy])

	const handleDownload = useCallback(() => {
		// Get the canvas element (hidden) and create download
		const canvas = document.querySelector(`canvas[data-qr-value="${value}"]`) as HTMLCanvasElement
		if (!canvas) return

		const url = canvas.toDataURL('image/png')
		const link = document.createElement('a')
		link.download = `qr-code-${Date.now()}.png`
		link.href = url
		link.click()
		onDownload?.()
	}, [value, onDownload])

	return (
		<div className="flex flex-col items-center gap-4">
			{title && <h4 className="font-medium text-center">{title}</h4>}

			{/* QR Code Display (SVG for better quality) */}
			<div className="p-4 bg-white rounded-lg shadow-sm">
				<QRCodeSVG
					value={value}
					size={size}
					level="M"
					marginSize={2}
					imageSettings={
						includeLogo
							? {
									src: polygrafIcon,
									height: size * 0.15,
									width: size * 0.15,
									excavate: true,
								}
							: undefined
					}
				/>
			</div>

			{/* Hidden canvas for download */}
			<div className="hidden">
				<QRCodeCanvas
					value={value}
					size={size * 2} // Higher resolution for download
					level="M"
					marginSize={2}
					data-qr-value={value}
				/>
			</div>

			{/* URL display */}
			<div className="w-full max-w-xs">
				<div className="flex items-center gap-2 p-2 bg-base-200 rounded-lg text-xs font-mono break-all">
					<span className="flex-1 truncate">{value}</span>
				</div>
			</div>

			{/* Action buttons */}
			<div className="flex gap-2">
				<Button
					variant="ghost"
					size="sm"
					onClick={handleCopyUrl}
					title={t('qrCode.copyLink')}
					className="border border-base-300"
					leadingIcon={copied ? <Check size={16} className="text-success" /> : <Copy size={16} />}
				>
					{copied ? t('common.copied') : t('qrCode.copy')}
				</Button>
				<Button
					variant="ghost"
					size="sm"
					onClick={handleDownload}
					title={t('qrCode.downloadQr')}
					className="border border-base-300"
					leadingIcon={<Download size={16} />}
				>
					{t('qrCode.download')}
				</Button>
			</div>
		</div>
	)
}

interface QRCodeModalProps {
	/** Whether the modal is open */
	isOpen: boolean
	/** Close handler */
	onClose: () => void
	/** The URL to encode */
	value: string
	/** Title for the modal */
	title?: string
	/** Description text */
	description?: string
}

/**
 * Modal wrapper for QR code display
 */
export function QRCodeModal({
	isOpen,
	onClose,
	value,
	title,
	description,
}: QRCodeModalProps) {
	const { t } = useTranslation()
	const displayTitle = title || t('qrCode.title')

	return (
		<Modal open={isOpen} onClose={onClose} title={displayTitle} description={description} size="sm">
			<QRCodeDisplay value={value} size={250} includeLogo />
		</Modal>
	)
}

interface QRCodeButtonProps {
	/** The URL to encode */
	value: string
	/** Modal title */
	title?: string
	/** Modal description */
	description?: string
	/** Button variant */
	variant?: 'primary' | 'ghost'
	/** Button size */
	size?: 'sm' | 'md' | 'lg'
	/** Show text label */
	showLabel?: boolean
}

/**
 * Button that opens a QR code modal
 */
export function QRCodeButton({
	value,
	title,
	description,
	variant = 'ghost',
	size = 'sm',
	showLabel = true,
}: QRCodeButtonProps) {
	const { t } = useTranslation()
	const [isOpen, setIsOpen] = useState(false)
	const iconSize = size === 'sm' ? 16 : size === 'lg' ? 24 : 20

	return (
		<>
			<Button
				variant={variant}
				size={size}
				onClick={() => setIsOpen(true)}
				title={t('qrCode.showQr')}
				aria-label={showLabel ? undefined : t('qrCode.showQr')}
				className={variant === 'ghost' ? 'border border-base-300' : undefined}
				leadingIcon={<QrCode size={iconSize} />}
			>
				{showLabel && t('qrCode.title')}
			</Button>

			<QRCodeModal
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				value={value}
				title={title}
				description={description}
			/>
		</>
	)
}
